import React, { useCallback, useEffect, useState } from 'react';
import {
  ClipboardDocumentIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { invitationsAPI, playersAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/errorMessage';
import type { CreatedInvitation, Invitation } from '../../types/invitation';

interface PlayerOption {
  id: number;
  name: string;
  ageGroup: string;
}

const STATUS_LABELS: Record<Invitation['status'], { text: string; className: string }> = {
  pending: { text: 'Függőben', className: 'bg-amber-100 text-amber-800' },
  accepted: { text: 'Elfogadva', className: 'bg-green-100 text-green-800' },
  expired: { text: 'Lejárt', className: 'bg-gray-100 text-gray-600' },
};

const formatDate = (value: string) => new Date(value).toLocaleDateString('hu-HU');

const describeRoles = (invitation: Pick<Invitation, 'isAdmin' | 'isCoach' | 'isPlayer' | 'isParent' | 'isGuest' | 'guestTeam'>) => {
  const labels: string[] = [];
  if (invitation.isAdmin) labels.push('Admin');
  if (invitation.isCoach) labels.push('Edző');
  if (invitation.isPlayer) labels.push('Játékos');
  if (invitation.isParent) labels.push('Szülő');
  if (invitation.isGuest) labels.push(invitation.guestTeam ? `Vendég (${invitation.guestTeam})` : 'Vendég');
  return labels.length > 0 ? labels.join(', ') : 'Felhasználó';
};

/**
 * Meghívók küldése és kezelése. Önkiszolgáló regisztráció nincs: a fiókok
 * csak innen kiküldött meghívóval jöhetnek létre, a szerepkört az admin adja meg.
 */
const InvitationsPanel: React.FC = () => {
  const { showToast } = useToast();

  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCoach, setIsCoach] = useState(true);
  const [isPlayer, setIsPlayer] = useState(false);
  const [isParent, setIsParent] = useState(false);
  const [isGuest, setIsGuest] = useState(false);
  const [guestTeam, setGuestTeam] = useState('');
  const [playerId, setPlayerId] = useState<string>('');
  const [players, setPlayers] = useState<PlayerOption[]>([]);
  const [sending, setSending] = useState(false);
  const [lastCreated, setLastCreated] = useState<CreatedInvitation | null>(null);

  const load = useCallback(async () => {
    try {
      setInvitations(await invitationsAPI.getAll());
    } catch (err) {
      showToast(getErrorMessage(err, 'A meghívókat nem sikerült betölteni.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!formOpen || players.length > 0) return;
    playersAPI
      .getAll()
      .then(setPlayers)
      .catch(() => showToast('A játékos listát nem sikerült betölteni.', 'error'));
  }, [formOpen, players.length, showToast]);

  const hasAnyRole = isAdmin || isCoach || isPlayer || isParent || isGuest;
  const needsPlayerLink = isPlayer || isParent;

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasAnyRole) return;

    setSending(true);
    setLastCreated(null);
    try {
      const created = await invitationsAPI.create({
        email: email.trim(),
        name: name.trim() || undefined,
        isAdmin,
        isCoach,
        isPlayer,
        isParent,
        isGuest,
        guestTeam: isGuest && guestTeam.trim() ? guestTeam.trim() : undefined,
        playerId: needsPlayerLink && playerId ? Number(playerId) : undefined,
      });

      setLastCreated(created);
      setEmail('');
      setName('');
      setPlayerId('');
      await load();

      showToast(
        created.emailSent
          ? 'Meghívó elküldve emailben.'
          : 'A meghívó elkészült, de az emailt nem sikerült kiküldeni - add át a linket kézzel.',
        created.emailSent ? 'success' : 'info'
      );
    } catch (err) {
      showToast(getErrorMessage(err, 'A meghívót nem sikerült elküldeni.'), 'error');
    } finally {
      setSending(false);
    }
  };

  const handleRevoke = async (invitation: Invitation) => {
    if (!window.confirm(`Biztosan visszavonod a meghívót? (${invitation.email})`)) return;

    try {
      await invitationsAPI.revoke(invitation.id);
      await load();
      showToast('Meghívó visszavonva.', 'info');
    } catch (err) {
      showToast(getErrorMessage(err, 'A meghívót nem sikerült visszavonni.'), 'error');
    }
  };

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      showToast('Link a vágólapra másolva.', 'info');
    } catch {
      showToast('A másolás nem sikerült - jelöld ki és másold kézzel.', 'error');
    }
  };

  const pendingCount = invitations.filter((i) => i.status === 'pending').length;

  return (
    <div className="mb-6 rounded-lg bg-white shadow">
      <div className="flex flex-col gap-3 border-b border-gray-100 p-4 sm:flex-row sm:items-center sm:justify-between md:p-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Meghívók</h2>
          <p className="text-sm text-gray-500">
            Ajánlott mód: meghívó. A meghívott maga állítja be a jelszavát. Jelszóval az „Új felhasználó” gombbal is létrehozhatsz fiókot.
            {pendingCount > 0 && ` Jelenleg ${pendingCount} függőben.`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setFormOpen((open) => !open)}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 sm:w-auto"
        >
          <EnvelopeIcon className="h-5 w-5" />
          {formOpen ? 'Bezárás' : 'Meghívó küldése'}
        </button>
      </div>

      {formOpen && (
        <form onSubmit={handleSend} className="space-y-4 border-b border-gray-100 p-4 md:p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="invite-email" className="mb-1 block text-sm font-medium text-gray-700">
                Email cím *
              </label>
              <input
                id="invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="pelda@email.hu"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
              />
            </div>

            <div>
              <label htmlFor="invite-name-field" className="mb-1 block text-sm font-medium text-gray-700">
                Név
              </label>
              <input
                id="invite-name-field"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Előre kitöltve a meghívottnak"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Szerepkörök * <span className="font-normal text-gray-400">(egyszerre több is választható)</span>
            </label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={isAdmin}
                  onChange={(e) => setIsAdmin(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Admin</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={isCoach}
                  onChange={(e) => setIsCoach(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Edző</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={isPlayer}
                  onChange={(e) => setIsPlayer(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Játékos</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={isParent}
                  onChange={(e) => setIsParent(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Szülő</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={isGuest}
                  onChange={(e) => {
                    setIsGuest(e.target.checked);
                    if (!e.target.checked) setGuestTeam('');
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Vendég</span>
              </label>
            </div>

            {isGuest && (
              <div className="mt-3">
                <label htmlFor="invite-guest-team" className="mb-1 block text-sm font-medium text-gray-700">
                  Külsős csapat neve
                </label>
                <input
                  type="text"
                  id="invite-guest-team"
                  maxLength={100}
                  value={guestTeam}
                  onChange={(e) => setGuestTeam(e.target.value)}
                  placeholder="pl. Szomszédvár FC"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Üresen hagyva belső kiszolgáló (gondnok, takarító, kitmanager).
                </p>
              </div>
            )}
            {!hasAnyRole && (
              <p className="mt-1 text-xs text-amber-600">Válassz legalább egy szerepkört.</p>
            )}
          </div>

          {needsPlayerLink && (
            <div>
              <label htmlFor="invite-player" className="mb-1 block text-sm font-medium text-gray-700">
                Játékoshoz kapcsolás
              </label>
              <select
                id="invite-player"
                value={playerId}
                onChange={(e) => setPlayerId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
              >
                <option value="">Nincs kapcsolás</option>
                {players.map((player) => (
                  <option key={player.id} value={player.id}>
                    {player.name} ({player.ageGroup})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">
                Játékos szerepkörnél magához a játékoshoz, szülőnél az első szabad szülő-helyre köti a fiókot.
              </p>
            </div>
          )}

          <p className="text-xs text-gray-400">
            A szerepkört a meghívó rögzíti - a meghívott nem tudja megváltoztatni. A link 7 napig
            érvényes, és csak egyszer használható fel.
          </p>

          <button
            type="submit"
            disabled={sending || !email.trim() || !hasAnyRole}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50 sm:w-auto"
          >
            <PaperAirplaneIcon className="h-4 w-4" />
            {sending ? 'Küldés...' : 'Meghívó küldése'}
          </button>

          {lastCreated && (
            <div className="rounded-lg bg-gray-50 p-3">
              <p className="text-sm font-medium text-gray-700">
                {lastCreated.emailSent ? 'Elküldve. A link biztonság kedvéért:' : 'A meghívó linkje:'}
              </p>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                <code className="min-w-0 flex-1 break-all rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600">
                  {lastCreated.acceptUrl}
                </code>
                <button
                  type="button"
                  onClick={() => handleCopyLink(lastCreated.acceptUrl)}
                  className="inline-flex shrink-0 items-center justify-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  Másolás
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-400">
                Ez a link most jelenik meg utoljára - később már nem kérhető le.
              </p>
            </div>
          )}
        </form>
      )}

      {loading ? (
        <div className="p-4 md:p-6">
          <div className="h-10 animate-pulse rounded-lg bg-gray-100" />
        </div>
      ) : invitations.length === 0 ? (
        <p className="p-4 text-sm text-gray-500 md:p-6">Még nem küldtél meghívót.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {invitations.map((invitation) => {
            const status = STATUS_LABELS[invitation.status];
            return (
              <li key={invitation.id} className="flex items-center justify-between gap-3 p-4 md:px-6">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-medium text-gray-900">
                      {invitation.email}
                    </span>
                    <span
                      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${status.className}`}
                    >
                      {status.text}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {describeRoles(invitation)}
                    {invitation.name && ` · ${invitation.name}`}
                    {' · '}
                    {invitation.status === 'accepted'
                      ? `Elfogadva: ${formatDate(invitation.acceptedAt!)}`
                      : `Érvényes: ${formatDate(invitation.expiresAt)}`}
                  </p>
                </div>

                {invitation.status !== 'accepted' && (
                  <button
                    type="button"
                    onClick={() => handleRevoke(invitation)}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-red-600"
                    aria-label="Meghívó visszavonása"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default InvitationsPanel;
